import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getTenant, masterGetRoles, masterCreateRole, masterUpdateRole, masterDeleteRole,
  getMasterPermissions, getTenantIntegrations, updateTenantIntegration, impersonate, deleteTenant,
  exportTenantDataByMaster,
} from '../../services/api';
import Toggle from '../../components/Toggle';
import { safeParseFeatures, safeParseJSON } from '../../context/AuthContext';
import { ALL_FEATURES } from './MasterTenantForm';
import { useConfirm } from '../../components/ConfirmDialog';
import { formatPhoneBR } from '../../utils/phone';

// ── Constants ──────────────────────────────────────────────────────────────────
const STATUS_BADGE = { active: 'badge-success', suspended: 'badge-warning', trial: 'badge-info', deleted: 'badge-gray' };
const STATUS_LABEL = { active: 'Ativo', suspended: 'Suspenso', trial: 'Trial', deleted: 'Excluído' };
// Label de feature: usa o registry oficial (MasterTenantForm) como fonte única.
// Qualquer feature registrada lá ganha label automaticamente aqui.
const featLabel = (k) => ALL_FEATURES[k]?.label || k;

const PERM_LABELS = {
  VIEW_ALL_APPOINTMENTS: 'Ver todos os agendamentos', VIEW_OWN_APPOINTMENTS: 'Ver próprios agendamentos',
  CREATE_APPOINTMENT: 'Criar agendamentos', EDIT_APPOINTMENT: 'Editar agendamentos', DELETE_APPOINTMENT: 'Excluir agendamentos',
  MANAGE_USERS: 'Gerenciar usuários', VIEW_BILLING: 'Ver faturamento', MANAGE_TENANT_SETTINGS: 'Configurar empresa',
  CONNECT_GOOGLE_CALENDAR: 'Google Calendar', VIEW_NOTIFICATIONS: 'Ver notificações', VIEW_AUDIT_LOGS: 'Ver auditoria',
};
const PERM_GROUPS = [
  { label: 'Agendamentos', perms: ['VIEW_ALL_APPOINTMENTS', 'VIEW_OWN_APPOINTMENTS', 'CREATE_APPOINTMENT', 'EDIT_APPOINTMENT', 'DELETE_APPOINTMENT'] },
  { label: 'Usuários',     perms: ['MANAGE_USERS'] },
  { label: 'Financeiro',   perms: ['VIEW_BILLING'] },
  { label: 'Config',       perms: ['MANAGE_TENANT_SETTINGS', 'CONNECT_GOOGLE_CALENDAR'] },
  { label: 'Outros',       perms: ['VIEW_NOTIFICATIONS', 'VIEW_AUDIT_LOGS'] },
];
const ROLE_EMPTY = { name: '', permissions: [] };

// ── Helper Components ──────────────────────────────────────────────────────────
function InfoRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, color: value ? 'var(--gray-900)' : 'var(--gray-300)', fontWeight: 500 }}>{value || '—'}</div>
    </div>
  );
}

function CopyBox({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => toast.error('Copiar manualmente'));
  };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
      <div style={{ flex: 1, background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '10px 14px', fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.6, color: 'var(--gray-700)', minWidth: 0 }}>
        {value}
      </div>
      <button className={`btn btn-sm ${copied ? 'btn-success' : 'btn-secondary'}`} style={{ flexShrink: 0, minWidth: 90 }} onClick={copy}>
        {copied ? '✓ Copiado!' : 'Copiar'}
      </button>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function MasterTenantView() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const confirm  = useConfirm();
  const [tab,    setTab]    = useState('dados');
  const [tenant, setTenant] = useState(null);
  const [roles,  setRoles]  = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [waActive, setWaActive]  = useState(false);
  const [waConfig, setWaConfig]  = useState({ provider: 'zapi', base_url: '', api_key: '', phone_number_id: '', access_token: '', markup_pct: 75 });
  const [waSaving, setWaSaving]  = useState(false);
  const [loading,  setLoading]   = useState(true);
  const [exporting, setExporting] = useState(false);

  // Role modal state
  const [roleModal,  setRoleModal]  = useState(false);
  const [editRole,   setEditRole]   = useState(null);
  const [roleForm,   setRoleForm]   = useState(ROLE_EMPTY);
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleActing, setRoleActing] = useState({});

  useEffect(() => {
    Promise.all([
      getTenant(id),
      masterGetRoles(id),
      getMasterPermissions(),
      getTenantIntegrations(id),
    ]).then(([tRes, rRes, pRes, iRes]) => {
      setTenant(tRes.data.tenant);
      setRoles(rRes.data.roles || []);
      setAllPerms(pRes.data.permissions || []);
      const wa = (iRes.data.integrations || []).find(i => i.type === 'whatsapp');
      if (wa) {
        setWaActive(!!wa.active);
        const cfg = safeParseJSON(wa.config, {});
        setWaConfig({
          provider:         cfg.provider || 'zapi',
          base_url:         cfg.base_url || cfg.api_url || '',
          api_key:          cfg.api_key  || cfg.token   || '',
          phone_number_id:  cfg.phone_number_id  || '',
          access_token:     cfg.access_token     || '',
          markup_pct:       cfg.markup_pct ?? 75,
        });
      }
    }).catch(() => toast.error('Erro ao carregar dados do cliente'))
      .finally(() => setLoading(false));
  }, [id]);

  const loadRoles = useCallback(() => {
    masterGetRoles(id).then(r => setRoles(r.data.roles || [])).catch(() => {});
  }, [id]);

  const doImpersonate = async () => {
    try {
      const res = await impersonate(id);
      if (res.data.token) localStorage.setItem('master_token', res.data.token);
      window.location.href = '/app';
    } catch { toast.error('Erro ao acessar cliente'); }
  };

  // Export de dados do tenant — antes era self-serve no painel do cliente,
  // agora restrito ao master. Usado para atender pedidos formais de
  // portabilidade (LGPD Art. 18 IV) ou rotinas internas de auditoria.
  const doExport = async () => {
    setExporting(true);
    try {
      const r = await exportTenantDataByMaster(id);
      const blob = r.data instanceof Blob ? r.data : new Blob([JSON.stringify(r.data)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = (tenant?.slug || 'atendoapp').replace(/[^a-z0-9-]/gi, '-');
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `${slug}-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Exportação gerada — confira seus downloads.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao exportar dados do cliente');
    } finally {
      setExporting(false);
    }
  };

  const doSoftDelete = async () => {
    const tenantName = tenant?.name || 'este cliente';
    const confirmed = await confirm({
      title: `Excluir "${tenantName}"?`,
      message: 'Esta ação é IRREVERSÍVEL. Ao excluir o cliente:',
      items: [
        'Ele some de todas as listagens e métricas',
        'Todos os usuários são desativados',
        'Agendamentos futuros são cancelados',
        'Faturas pendentes são canceladas',
        'Integrações (WhatsApp, Google Calendar) são desativadas',
        'Ele NÃO poderá voltar a ser ativo — só recadastrando do zero',
      ],
      hint: 'Se a ideia é bloquear temporariamente (ex.: inadimplência), use Suspender — preserva todos os dados.',
      confirmText: 'Sim, excluir',
      cancelText: 'Cancelar',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      const r = await deleteTenant(id);
      const d = r.data.deactivated || {};
      toast.success(
        `Cliente excluído. ${d.users || 0} usuário(s), ${d.appointments || 0} agendamento(s) e ${d.invoices || 0} fatura(s) desativados.`,
        { duration: 6000 }
      );
      navigate('/master/tenants');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao excluir cliente');
    }
  };

  const openNewRole  = () => { setEditRole(null); setRoleForm(ROLE_EMPTY); setRoleModal(true); };
  const openEditRole = r  => { setEditRole(r); setRoleForm({ name: r.name, permissions: [...r.permissions] }); setRoleModal(true); };
  const togglePerm   = p  => setRoleForm(f => ({
    ...f,
    permissions: f.permissions.includes(p) ? f.permissions.filter(x => x !== p) : [...f.permissions, p],
  }));

  const handleRoleSave = async e => {
    e.preventDefault();
    if (!roleForm.name.trim()) return toast.error('Nome do perfil é obrigatório');
    setRoleSaving(true);
    try {
      if (editRole) { await masterUpdateRole(id, editRole.id, roleForm); toast.success('Perfil atualizado!'); }
      else          { await masterCreateRole(id, roleForm); toast.success('Perfil criado!'); }
      setRoleModal(false);
      loadRoles();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar perfil'); }
    finally { setRoleSaving(false); }
  };

  const handleRoleDelete = async role => {
    const ok = await confirm({
      title: `Excluir perfil "${role.name}"?`,
      message: 'Esta ação não pode ser desfeita. Usuários vinculados a este perfil ficarão sem perfil definido.',
      confirmText: 'Excluir perfil',
      variant: 'danger',
    });
    if (!ok) return;
    setRoleActing(a => ({ ...a, [role.id]: true }));
    try {
      await masterDeleteRole(id, role.id);
      toast.success('Perfil excluído!');
      loadRoles();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro'); }
    finally { setRoleActing(a => ({ ...a, [role.id]: false })); }
  };

  const handleWaSave = async e => {
    e.preventDefault();
    const isMetaCloud = waConfig.provider === 'meta_cloud';
    if (isMetaCloud && (!waConfig.phone_number_id.trim() || !waConfig.access_token.trim()))
      return toast.error('Phone Number ID e Access Token são obrigatórios para Meta Cloud API');
    if (!isMetaCloud && (!waConfig.base_url.trim() || !waConfig.api_key.trim()))
      return toast.error('URL da API e chave de API são obrigatórios');
    setWaSaving(true);
    try {
      await updateTenantIntegration(id, 'whatsapp', { active: waActive, config: waConfig });
      toast.success('Integração WhatsApp salva!');
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar integração'); }
    finally { setWaSaving(false); }
  };

  if (loading) return <div className="page-body"><div className="spinner" style={{ margin: '60px auto', display: 'block' }} /></div>;

  const features = safeParseFeatures(tenant?.features);
  const activeFeats = Object.entries(features).filter(([, v]) => v).map(([k]) => k);
  const hasWa = features.whatsapp;

  const appUrl   = window.location.origin;
  const loginUrl = `${appUrl}/login?tenant=${encodeURIComponent(tenant?.slug || '')}`;
  const embedHtml = tenant?.widget_token ? `<script src="${appUrl}/api/widget/${tenant.widget_token}/embed.js" async></script>` : null;

  const TABS = [
    { key: 'dados',    label: '📋 Dados' },
    { key: 'widget',   label: '🔗 Widget' },
    { key: 'perfis',   label: `🛡️ Perfis (${roles.length})` },
    ...(hasWa ? [{ key: 'whatsapp', label: '💬 WhatsApp' }] : []),
  ];

  return (
    <div className="page-body">
      {/* Header */}
      <div className="page-header-row">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
          {tenant?.logo_url
            ? <img src={tenant.logo_url} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', flexShrink: 0, border: '1px solid var(--gray-200)' }} />
            : <div style={{ width: 48, height: 48, borderRadius: 10, background: tenant?.primary_color || 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'white', fontSize: 20, flexShrink: 0 }}>
                {(tenant?.name || 'A')[0].toUpperCase()}
              </div>
          }
          <div className="page-header" style={{ marginBottom: 0 }}>
            <h2>{tenant?.name}</h2>
            <p>
              <code style={{ fontSize: 11, background: 'var(--gray-100)', padding: '1px 6px', borderRadius: 4 }}>{tenant?.slug}</code>
              &nbsp;
              <span className={`badge ${STATUS_BADGE[tenant?.status] || 'badge-gray'}`}>{STATUS_LABEL[tenant?.status] || tenant?.status}</span>
            </p>
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/master/tenants')}>← Voltar</button>
          <button className="btn btn-secondary btn-sm" onClick={doImpersonate}>👁 Acessar</button>
          <button
            className={`btn btn-secondary btn-sm${exporting ? ' btn-loading' : ''}`}
            onClick={doExport}
            disabled={exporting}
            title="Gerar arquivo JSON completo com todos os dados deste cliente (uso interno / pedidos LGPD)"
          >
            {exporting ? 'Exportando...' : '📥 Exportar dados'}
          </button>
          <Link to={`/master/tenants/${id}/edit`} className="btn btn-primary btn-sm">✏️ Editar</Link>
          {tenant?.status !== 'suspended' && (
            <button className="btn btn-danger btn-sm" onClick={doSoftDelete}>🗑️ Excluir</button>
          )}
        </div>
      </div>

      {/* Tab Bar */}
      <div className="tab-bar">
        {TABS.map(t => (
          <button key={t.key} className={`tab-item${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Dados ── */}
      {tab === 'dados' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Badge de origem — destaque para auto-conversão via landing */}
          {tenant?.created_via === 'self_serve_signup' && (
            <div className="info-box" style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '12px 16px', borderRadius: 10, fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>🚀</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>Auto-convertido via landing</div>
                <div style={{ fontSize: 12, opacity: 0.95 }}>
                  Este cliente começou como lead e criou a conta sozinho via
                  <code style={{ background: 'rgba(255,255,255,0.2)', padding: '1px 6px', borderRadius: 4, margin: '0 4px' }}>/signup</code>
                  (plano Starter).
                  <Link to="/master/leads" style={{ color: '#fff', textDecoration: 'underline', marginLeft: 4 }}>Ver lead original</Link>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header"><span className="card-title">Dados cadastrais</span></div>
            <div className="card-body grid-2" style={{ marginTop: 4 }}>
              <InfoRow label="Nome"     value={tenant?.name} />
              <InfoRow label="E-mail"   value={tenant?.email} />
              <InfoRow label="Telefone" value={formatPhoneBR(tenant?.phone)} />
              <InfoRow label="Site"     value={tenant?.website} />
              <InfoRow label="Plano"    value={tenant?.plan_name} />
              <InfoRow label="Status"   value={STATUS_LABEL[tenant?.status] || tenant?.status} />
              <InfoRow label="Origem"   value={
                tenant?.created_via === 'self_serve_signup' ? 'Cadastro automático (landing)'
                : tenant?.created_via === 'seed' ? 'Seed (dev/staging)'
                : tenant?.created_via === 'import' ? 'Importado'
                : 'Manual (master)'
              } />
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">🎨 Identidade visual</span></div>
            <div className="card-body">
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: tenant?.primary_color || '#2563eb', border: '1px solid var(--gray-200)', flexShrink: 0 }} title="Cor primária" />
                <div style={{ width: 36, height: 36, borderRadius: 8, background: tenant?.secondary_color || '#1e40af', border: '1px solid var(--gray-200)', flexShrink: 0 }} title="Cor secundária" />
                <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>{tenant?.primary_color} / {tenant?.secondary_color}</span>
              </div>
              {tenant?.logo_url && (
                <img src={tenant.logo_url} alt="logo" style={{ height: 56, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--gray-200)', padding: 8, background: 'var(--surface)' }} />
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">⚙️ Features</span></div>
            <div className="card-body">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(features).map(([k, v]) => (
                  <span key={k} className={`feature-tag${v ? '' : ' off'}`} style={{ fontSize: 12 }}>
                    {v ? '✓' : '✕'} {featLabel(k)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Widget ── */}
      {tab === 'widget' && (
        <div className="card">
          <div className="card-header"><span className="card-title">🔗 Área do Cliente — Links para compartilhar</span></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>🔑 Link direto de acesso</div>
              <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>
                O cliente abre esse link e já vê o sistema com as cores e logo da empresa. Ideal para WhatsApp, e-mail ou redes sociais.
              </p>
              <CopyBox value={loginUrl} />
            </div>

            {embedHtml && (
              <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>📦 Widget para inserir no site do cliente</div>
                <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>
                  Cole essa tag no <code style={{ background: 'var(--gray-100)', padding: '1px 5px', borderRadius: 4 }}>&lt;head&gt;</code> ou antes do <code style={{ background: 'var(--gray-100)', padding: '1px 5px', borderRadius: 4 }}>&lt;/body&gt;</code> do site.
                  Um botão "Área do Cliente" aparecerá automaticamente no menu com as cores configuradas.
                </p>
                <CopyBox value={embedHtml} />
                <div className="info-box" style={{ marginTop: 10, fontSize: 12 }}>
                  <strong>Segurança dupla:</strong> o widget só funciona se a conta estiver ativa e se o código estiver inserido no domínio cadastrado no campo <em>Site</em> do cadastro.
                  {tenant?.website ? <> Domínio configurado: <strong>{tenant.website}</strong></> : <> <strong>Atenção:</strong> nenhum site cadastrado — o widget aceita qualquer domínio.</>}
                </div>
              </div>
            )}

            {!embedHtml && (
              <div className="warning-box" style={{ fontSize: 13 }}>
                Widget não disponível. O token ainda não foi gerado para este cliente. Acesse <Link to={`/master/tenants/${id}/edit`}>Editar</Link> e salve para gerar.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Perfis ── */}
      {tab === 'perfis' && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="card-title">🛡️ Perfis & Permissões</span>
            <button className="btn btn-primary btn-sm" onClick={openNewRole}>+ Novo Perfil</button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {roles.length === 0
              ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>Nenhum perfil cadastrado</div>
              : roles.map(role => (
                <div key={role.id} style={{ padding: '14px 16px', borderBottom: '1px solid var(--gray-100)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {role.name}
                        {role.is_system && <span className="badge badge-gray" style={{ fontSize: 10 }}>sistema</span>}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                        {role.permissions.length === 0
                          ? <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>Sem permissões</span>
                          : role.permissions.map(p => (
                            <span key={p} style={{ fontSize: 11, background: 'var(--gray-100)', borderRadius: 4, padding: '2px 6px', color: 'var(--gray-600)' }}>
                              {PERM_LABELS[p] || p}
                            </span>
                          ))
                        }
                      </div>
                    </div>
                    {!role.is_system && (
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEditRole(role)}>Editar</button>
                        <button className={`btn btn-danger btn-sm${roleActing[role.id] ? ' btn-loading' : ''}`}
                          disabled={roleActing[role.id]} onClick={() => handleRoleDelete(role)}>
                          {roleActing[role.id] ? '' : 'Excluir'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* ── Tab: WhatsApp ── */}
      {tab === 'whatsapp' && hasWa && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="card-title">💬 WhatsApp — Configuração</span>
              <span className={`badge ${waActive ? 'badge-success' : 'badge-gray'}`}>{waActive ? 'Ativo' : 'Inativo'}</span>
            </div>
            <Toggle checked={waActive} onChange={() => setWaActive(a => !a)} />
          </div>
          <div className="card-body">
            <form onSubmit={handleWaSave}>
              <div className="form-group">
                <label>Provedor</label>
                <select value={waConfig.provider} onChange={e => setWaConfig(c => ({ ...c, provider: e.target.value }))}>
                  <option value="meta_cloud">✅ Meta Cloud API (oficial — sem risco de ban)</option>
                  <option value="zapi">Z-API (não oficial)</option>
                  <option value="evolution">Evolution API (não oficial)</option>
                  <option value="generic">Genérico (HTTP POST)</option>
                </select>
              </div>

              {waConfig.provider === 'meta_cloud' ? (
                <>
                  <div style={{ background:'rgba(37,99,235,0.06)', border:'1px solid var(--primary)', borderRadius:8, padding:'12px 14px', marginBottom:14, fontSize:12 }}>
                    <strong>Meta Cloud API</strong> — licenciada pela Meta, sem risco de ban.
                    Configure em: Meta Business Manager → WhatsApp → API Setup.
                    <br /><br />
                    Crie um <em>System User</em> com permissão <code>whatsapp_business_messaging</code> e gere um token permanente.
                  </div>
                  <div className="form-group">
                    <label>Phone Number ID *</label>
                    <input value={waConfig.phone_number_id} onChange={e => setWaConfig(c => ({ ...c, phone_number_id: e.target.value }))}
                      placeholder="123456789012345" />
                    <small>Encontre em: Meta Business Manager → WhatsApp → Números de telefone</small>
                  </div>
                  <div className="form-group">
                    <label>Access Token permanente *</label>
                    <input type="password" value={waConfig.access_token} onChange={e => setWaConfig(c => ({ ...c, access_token: e.target.value }))}
                      placeholder="EAAxxxx..." />
                    <small>Gere um token permanente via System User no Meta Business Manager</small>
                  </div>
                  <div className="info-box" style={{ marginBottom: 12, fontSize: 12 }}>
                    Configure o webhook no Meta Business Manager com a URL abaixo. Use qualquer string como <em>Verify Token</em> (não é verificado nesta versão):<br />
                    <code style={{ wordBreak: 'break-all' }}>{window.location.origin}/api/webhooks/whatsapp/{tenant?.slug}</code>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>URL base da API *</label>
                    <input value={waConfig.base_url} onChange={e => setWaConfig(c => ({ ...c, base_url: e.target.value }))}
                      placeholder={waConfig.provider === 'zapi' ? 'https://api.z-api.io/instances/ID/token/TOKEN' : waConfig.provider === 'evolution' ? 'https://evolution.suaempresa.com' : 'https://sua-api.com/enviar'} />
                  </div>
                  <div className="form-group">
                    <label>{waConfig.provider === 'zapi' ? 'Client-Token' : waConfig.provider === 'evolution' ? 'API Key' : 'Token / API Key'} *</label>
                    <input value={waConfig.api_key} onChange={e => setWaConfig(c => ({ ...c, api_key: e.target.value }))}
                      placeholder="sua-chave-aqui" />
                  </div>
                  {waConfig.provider === 'zapi' && (
                    <div style={{ background:'var(--gray-50)', border:'1px solid var(--gray-200)', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12 }}>
                      <strong>Z-API:</strong> Copie a URL base da instância e o <em>Client-Token</em> do painel Z-API. ⚠️ Não licenciado pela Meta.
                    </div>
                  )}
                  {waConfig.provider === 'evolution' && (
                    <div style={{ background:'var(--gray-50)', border:'1px solid var(--gray-200)', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:12 }}>
                      <strong>Evolution API:</strong> URL do servidor e API Key da instância. ⚠️ Não licenciado pela Meta.
                    </div>
                  )}
                  <div className="info-box" style={{ marginBottom: 12, fontSize: 12 }}>
                    Webhook para receber respostas (SIM/NÃO):<br />
                    <code style={{ wordBreak: 'break-all' }}>{window.location.origin}/api/webhooks/whatsapp/{tenant?.slug}</code>
                  </div>
                </>
              )}

              {/* Markup de cobrança — para todos os provedores */}
              <div style={{ borderTop:'1px solid var(--gray-100)', paddingTop:14, marginBottom:14 }}>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>💰 Repasse de custo ao cliente</div>
                <p style={{ fontSize:12, color:'var(--gray-500)', marginBottom:10 }}>
                  Para a <strong>Meta Cloud API</strong>, o custo por mensagem é calculado automaticamente e adicionado à fatura mensal com esta margem.
                  Para outros provedores, configure manualmente nas faturas.
                </p>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <label style={{ fontSize:13, color:'var(--gray-700)', whiteSpace:'nowrap' }}>Margem de lucro (%):</label>
                  <input type="number" min={0} max={500} step={1}
                    value={waConfig.markup_pct}
                    onChange={e => setWaConfig(c => ({ ...c, markup_pct: parseFloat(e.target.value) || 0 }))}
                    style={{ width:90 }} />
                  <span style={{ fontSize:12, color:'var(--gray-500)' }}>
                    Ex: custo R$0,23 + {waConfig.markup_pct}% = <strong>R${(0.232 * (1 + (waConfig.markup_pct||0) / 100)).toFixed(2)}</strong> cobrado do cliente
                  </span>
                </div>
              </div>
              <button type="submit" className={`btn btn-primary btn-sm${waSaving ? ' btn-loading' : ''}`} disabled={waSaving}>
                {waSaving ? 'Salvando...' : 'Salvar WhatsApp'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Role modal ── */}
      {roleModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setRoleModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>{editRole ? 'Editar Perfil' : 'Novo Perfil'}</h3>
              <button className="modal-close" onClick={() => setRoleModal(false)}>✕</button>
            </div>
            <form onSubmit={handleRoleSave}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome do perfil *</label>
                  <input value={roleForm.name} onChange={e => setRoleForm(f => ({ ...f, name: e.target.value }))} autoFocus placeholder="Ex: Atendente, Gerente..." />
                </div>
                <div className="form-group">
                  <label>Permissões</label>
                  {PERM_GROUPS.map(group => (
                    <div key={group.label} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', marginBottom: 6 }}>{group.label}</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {group.perms.map(p => {
                          const exists = allPerms.some(ap => ap.name === p);
                          if (!exists) return null;
                          return (
                            <div key={p}
                              onClick={() => togglePerm(p)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                                borderRadius: 8, border: '1.5px solid',
                                borderColor: roleForm.permissions.includes(p) ? 'var(--primary)' : 'var(--gray-200)',
                                background: roleForm.permissions.includes(p) ? 'rgba(37,99,235,0.06)' : 'transparent',
                                cursor: 'pointer', transition: 'all .15s', fontSize: 13,
                              }}>
                              <Toggle checked={roleForm.permissions.includes(p)} onChange={() => {}} />
                              <span>{PERM_LABELS[p] || p}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setRoleModal(false)}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${roleSaving ? ' btn-loading' : ''}`} disabled={roleSaving}>
                  {roleSaving ? 'Salvando...' : editRole ? 'Salvar' : 'Criar Perfil'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
